import type { NavItem } from './navigationItems';

/**
 * Get the primary permission from a NavItem
 * Returns the first permission from the permissions array, or null if none
 */
export const getNavItemPermission = (item: NavItem): string | null => {
	if (item.permissions && item.permissions.length > 0) {
		// Return the first permission as the primary identifier
		return item.permissions[0];
	}
	return null;
};

/**
 * Find a NavItem by its href and return it with parent permission context
 * Recursively searches through navigation items and submenus
 * Returns the item and its parent's permission if the item doesn't have its own
 */
export const findNavItemByHrefWithParent = (
	items: NavItem[],
	href: string,
	parentPermission: string | null = null,
): { item: NavItem | null; permission: string | null } => {
	for (const item of items) {
		// Get current item's permission (or use parent's if none)
		const currentPermission = getNavItemPermission(item) || parentPermission;

		// Check if this item matches the href
		if (item.href === href) {
			// If item has its own permission, use it; otherwise use parent's
			const permission = getNavItemPermission(item) || parentPermission;
			return { item, permission };
		}

		// Recursively search submenu with current permission as parent
		if (item.submenu) {
			const found = findNavItemByHrefWithParent(
				item.submenu,
				href,
				currentPermission,
			);
			if (found.item) {
				return found;
			}
		}
	}
	return { item: null, permission: null };
};

/**
 * Find a NavItem by its href
 * Recursively searches through navigation items and submenus
 */
export const findNavItemByHref = (
	items: NavItem[],
	href: string,
): NavItem | null => {
	const result = findNavItemByHrefWithParent(items, href);
	return result.item;
};

/**
 * Get permission for a nav item, using parent permission as fallback
 * This is used when adding quick actions for submenu items without their own permissions
 */
export const getNavItemPermissionWithParent = (
	item: NavItem,
	parentPermission: string | null,
): string | null => {
	return getNavItemPermission(item) || parentPermission;
};

/**
 * Flatten navigation items to get all actionable items (items with href)
 */
export const flattenNavItems = (items: NavItem[]): NavItem[] => {
	const result: NavItem[] = [];

	items.forEach((item) => {
		// Add item if it has href
		if (item.href) {
			result.push(item);
		}

		// Recursively process submenu items
		if (item.submenu) {
			result.push(...flattenNavItems(item.submenu));
		}
	});

	return result;
};

/**
 * Convert a NavItem to a QuickAction-like object for display
 * This is used for mapping hrefs back to display data
 */
export const navItemToDisplayData = (item: NavItem) => {
	return {
		title: item.title,
		href: item.href,
		icon: item.icon,
		permissions: item.permissions,
	};
};
